import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import CompleteModal from './CompleteModal';
import CouponService from '../../../../service/coupon/Coupon';
import { isLogin } from '../../../../utils/auth';
import { useModal } from '../../../../context/ModalContext';

export interface RandomCoupon {
  name: string;
  description: string;
  description2: string;
  validDays: number;
  valid_days?: number;
  expireText?: string;
}

/**

/**
 * ScratchCoupon
 * - base 이미지: 초록 쿠폰 (/assets/images/promotion/lucky/coupon.png)
 * - overlay 이미지: 긁기 전 은박 (/assets/images/promotion/lucky/coupon-before.png)
 * - 캔버스를 overlay로 올려놓고, 마우스/터치로 긁으면 'destination-out'로 지움
 * - REVEAL_THRESHOLD 이상 긁히면 자동 완료 처리
 */
const REVEAL_THRESHOLD = 0.4; // 55%
const BRUSH_RADIUS = 25; // 긁는 반경(px)

export default function ScratchCoupon() {
  const { modalDispatch } = useModal();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const [isDown, setIsDown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const [coupon, setCoupon] = useState<RandomCoupon | null>(null);
  const [couponChecked, setCouponChecked] = useState(false);

  const addDaysLocal = (base: Date, days: number) => {
    // 로컬 자정으로 맞춘 뒤 일수 더하기 → 타임존/서머타임 영향 최소화
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    d.setDate(d.getDate() + days);
    return d;
  };

  const formatYmd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  };

  // 랜덤 쿠폰 생성
  const getCoupon = async () => {
    try {
      const res = await CouponService.random();
      const raw: RandomCoupon = res.data;
      const expireText = formatYmd(addDaysLocal(new Date(), raw.validDays));

      setCoupon({ ...raw, expireText });
    } catch (error) {
      console.log(error);
    }
  };

  // 쿠폰 가능 여부
  const checkCoupon = async () => {
    try {
      const res = await CouponService.checkRandom();

      if (res.data) {
        const raw: RandomCoupon = res.data;
        const expireText = formatYmd(
          addDaysLocal(new Date(), raw.valid_days ?? 0),
        );
        setCoupon({ ...raw, expireText });
        setIsCompleted(true);
      } else {
        setIsCompleted(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setCouponChecked(true);
    }
  };

  useEffect(() => {
    checkCoupon();
  }, []);

  // 이미지 로드 & 캔버스 세팅 (DPR 반영)
  useEffect(() => {
    if (!couponChecked) return;
    const canvas = canvasRef.current!;
    const wrapper = wrapperRef.current!;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    ctxRef.current = ctx;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const { width, height } = wrapper.getBoundingClientRect();

    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.scale(dpr, dpr);

    if (isCompleted) {
      ctx.clearRect(0, 0, width, height);
      return;
    }

    // overlay(긁기 전) 이미지를 캔버스에 그려서 시작
    const overlayImg = new Image();
    overlayImg.src = '/assets/images/promotion/lucky/coupon-before.png';
    overlayImg.onload = () => {
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(overlayImg, 0, 0, width, height);
    };
  }, [couponChecked]);

  // 좌표 얻기
  const getPos = (e: MouseEvent | TouchEvent) => {
    const rect = wrapperRef.current!.getBoundingClientRect();
    if ('touches' in e) {
      const t = e.touches[0];
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    }
    const m = e as MouseEvent;
    return { x: m.clientX - rect.left, y: m.clientY - rect.top };
  };

  // 지우개 그리기
  const scratchAt = (x: number, y: number) => {
    const ctx = ctxRef.current!;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, BRUSH_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  // 얼마나 벗겨졌는지 추정
  const checkRevealed = async () => {
    const canvas = canvasRef.current!;
    const ctx = ctxRef.current!;
    const { width, height } = canvas;
    // 성능 위해 샘플링 간격 늘림
    const sample = 8;
    const { data } = ctx.getImageData(0, 0, width, height);
    let cleared = 0;
    let total = 0;

    for (let y = 0; y < height; y += sample) {
      for (let x = 0; x < width; x += sample) {
        const idx = (y * width + x) * 4 + 3; // alpha
        total++;
        if (data[idx] < 10) cleared++;
      }
    }
    const ratio = cleared / total;
    if (ratio >= REVEAL_THRESHOLD) {
      setIsCompleted(true);
      setShowCompleteModal(true);
      // 전부 제거
      ctx.clearRect(0, 0, width, height);
      await getCoupon();
    }
  };

  // 이벤트 핸들러
  const handleDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isLogin()) {
      modalDispatch({ type: 'USER_ALERT', payload: 'guest' });
      return;
    }
    if (isCompleted) return;
    setIsDown(true);
    setIsFocused(true);
    const pos = getPos(e.nativeEvent as any);
    scratchAt(pos.x, pos.y);
  };
  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDown || isCompleted) return;
    const pos = getPos(e.nativeEvent as any);
    scratchAt(pos.x, pos.y);
  };
  const handleUp = () => {
    if (!isDown) return;
    setIsDown(false);
    checkRevealed();
  };

  return (
    <Wrap>
      {/* 베이스: 초록 쿠폰 이미지 */}
      <Ticket ref={wrapperRef}>
        <BaseImage
          src="/assets/images/promotion/lucky/coupon.png"
          alt="coupon"
          draggable={false}
        />

        {/* 베이스 위 텍스트 (완료시 보이는 내용) */}
        {coupon && (
          <AfterText $show={isFocused || isCompleted}>
            <AfterTitle>
              {coupon.name}
              <AfterSub>{coupon.description2}</AfterSub>
            </AfterTitle>
            <AfterDate>
              사용기한 {coupon.expireText}
              까지
            </AfterDate>
          </AfterText>
        )}

        {/* 처음 안내 텍스트 (긁기 전) */}
        <BeforeText $hide={isFocused || isCompleted}>
          <BeforeSmall>복권 긁고 쿠폰 확인</BeforeSmall>
          <BeforeBig>마우스로 복권을 긁어보세요!</BeforeBig>
        </BeforeText>

        {/* 스크래치 레이어 */}
        <ScratchLayer
          ref={canvasRef}
          onMouseDown={handleDown as any}
          onMouseMove={handleMove as any}
          onMouseUp={handleUp}
          onMouseLeave={handleUp}
          onTouchStart={handleDown as any}
          onTouchMove={handleMove as any}
          onTouchEnd={handleUp}
        />
      </Ticket>
      {showCompleteModal && coupon && (
        <CompleteModal
          coupon={coupon}
          onClose={() => setShowCompleteModal(false)}
        />
      )}
    </Wrap>
  );
}

/* styled-components */

const Wrap = styled.div`
  width: 312px;
`;

const Ticket = styled.div`
  position: relative;
  width: 100%;
  height: 134px;
  border-radius: 24px;
  overflow: hidden;
`;

const BaseImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  pointer-events: none;
`;

const ScratchLayer = styled.canvas`
  position: absolute;
  inset: 0;
  cursor: grab;
  z-index: 3;
  touch-action: none;
`;

const BeforeText = styled.div<{ $hide?: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: ${({ $hide }) => ($hide ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  pointer-events: none;
`;
const BeforeSmall = styled.div`
  color: #6a6a6a;
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 19.6px */
  letter-spacing: -0.28px;
`;
const BeforeBig = styled.div`
  color: #222;
  text-align: center;
  font-family: Paperlogy;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%; /* 28px */
`;

const AfterText = styled.div<{ $show?: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: ${({ $show }) => ($show ? 'flex' : 'none')};
  align-items: flex-start;
  padding: 32px 27.5px;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  pointer-events: none;
`;

const AfterTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  color: #000;
  font-family: 'Wanted Sans';
  font-size: 20px;
  font-weight: 900;
  line-height: 130%; /* 72.8px */
`;
const AfterSub = styled.div`
  font-weight: 700;
  font-size: 15px;
  line-height: 130%;
`;
const AfterDate = styled.div`
  color: #565b56;
  text-align: center;
  font-family: 'Wanted Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 31.2px */
`;
